// Request schemas of the Regeln-&-Produkte screen (spec §1.12). Task 51 appends the rule schemas.
import { z } from 'zod'
import { isoDateSchema } from './tenant'

export const productCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Z][A-Z0-9_]{1,31}$/, 'Produkt-Kürzel: 2–32 Zeichen aus A–Z, 0–9 und _, beginnend mit einem Buchstaben.')

export const upsertProductSchema = z.object({
  code: productCodeSchema,
  name: z.string().trim().min(1, 'Name fehlt.').max(120),
  active: z.boolean().optional(),
  sort_order: z.number().int().min(0).max(9999).optional(),
})
export type UpsertProductBody = z.infer<typeof upsertProductSchema>

export const replaceTermsSchema = z.object({
  terms: z.array(z.string().trim().max(120)).max(100, 'Höchstens 100 Suchbegriffe pro Produkt.'),
})
export type ReplaceTermsBody = z.infer<typeof replaceTermsSchema>

export const matchTestSchema = z.object({
  invoice_name: z.string().trim().min(1, 'Bitte einen Invoice-Namen eingeben.').max(300),
})
export type MatchTestBody = z.infer<typeof matchTestSchema>

export const conditionSchema = z.object({
  field: z.enum([
    'deal.closer_tc_id',
    'deal.setter_tc_id',
    'deal.host_tc_id',
    'deal.host_rate_bp',
    'deal.overhead_individual_cents',
    'deal.lead_source',
    'deal.sonstige_tc_id',
  ]),
  op: z.enum(['eq', 'ne', 'gt', 'in']),
  value: z.union([z.string(), z.number(), z.array(z.union([z.string(), z.number()]))]),
})
export type ConditionBody = z.infer<typeof conditionSchema>

const ruleFields = z.object({
  key: z.string().trim().regex(/^[A-Z][A-Z0-9_]{1,63}$/, 'Regel-Schlüssel: Großbuchstaben, Ziffern und _, 2–64 Zeichen.'),
  name: z.string().trim().min(1, 'Name fehlt.').max(120),
  role_code: z.string().trim().regex(/^[A-Z][A-Z0-9_]{1,31}$/, 'Rollen-Code: Großbuchstaben, Ziffern und _, 2–32 Zeichen.'),
  role_label: z.string().trim().min(1, 'Rollenbezeichnung fehlt.').max(120),
  leistung_text: z.string().trim().min(1, 'Leistungstext fehlt.').max(200),
  product_codes: z.array(productCodeSchema).min(1, 'Mindestens ein Produkt oder „alle Produkte“.').nullable(),
  recipient_mode: z.enum(['deal_role', 'fixed']),
  recipient_deal_role: z.enum(['closer', 'setter', 'host', 'sonstige']).nullable().default(null),
  recipient_tc_id: z.string().trim().min(1).nullable().default(null),
  amount_mode: z.enum(['fixed', 'pct_of_net', 'deal_host_rate', 'deal_overhead_individual']),
  amount_cents: z.number().int().min(-100_000_000).max(100_000_000).nullable().default(null),
  rate_bp: z.number().int().min(0).max(1_000_000).nullable().default(null),
  applies_to: z.enum(['first_rate', 'every_rate']),
  conditions: z.array(conditionSchema).max(10).default([]),
  unassigned_policy: z.enum(['emit', 'skip']).default('emit'),
  valid_from: isoDateSchema.optional(),
  valid_to: isoDateSchema.nullable().default(null),
  sort_order: z.number().int().min(0).max(9999).default(100),
})

/**
 * The CHECK constraints of commission_rules, restated in German before the row reaches D1. The validity
 * window is only checked here when the body carries both ends; an edit may omit `valid_from` and inherit
 * it from the edited row, so the binding check runs in `ruleRowFrom` against the effective start date.
 */
function checkRule(v: z.infer<typeof ruleFields>, ctx: z.RefinementCtx): void {
  if (v.recipient_mode === 'deal_role' && v.recipient_deal_role === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recipient_deal_role'], message: 'Bitte die Rolle des Abschlusses wählen.' })
  }
  if (v.recipient_mode === 'deal_role' && v.recipient_tc_id !== null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recipient_tc_id'], message: 'Entweder Rolle des Abschlusses oder feste Person, nicht beides.' })
  }
  if (v.recipient_mode === 'fixed' && v.recipient_tc_id === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recipient_tc_id'], message: 'Bitte die feste Person wählen.' })
  }
  if (v.recipient_mode === 'fixed' && v.recipient_deal_role !== null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recipient_deal_role'], message: 'Bei fester Person darf keine Abschluss-Rolle gesetzt sein.' })
  }
  if (v.amount_mode === 'fixed' && (v.amount_cents === null || v.amount_cents === 0)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['amount_cents'], message: 'Bitte einen Betrag ungleich 0 angeben.' })
  }
  if (v.amount_mode === 'pct_of_net' && v.rate_bp === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rate_bp'], message: 'Bitte den Prozentsatz angeben.' })
  }
  if (v.valid_from !== undefined && v.valid_to !== null && v.valid_to <= v.valid_from) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['valid_to'], message: '„Gültig bis“ muss nach „Gültig ab“ liegen.' })
  }
}

export const upsertRuleSchema = ruleFields.superRefine(checkRule)
export type UpsertRuleBody = z.infer<typeof upsertRuleSchema>

/** The dry run takes the same body plus, for an edit, the id of the row being edited. */
export const dryRunRuleSchema = ruleFields.extend({ id: z.string().trim().min(1).nullable().default(null) }).superRefine(checkRule)
export type DryRunRuleBody = z.infer<typeof dryRunRuleSchema>
